import { IconButton } from '@/components/buttons'
import LoadMoreButton, { useLoadMore } from '@/components/buttons/LoadMoreButton'
import { ImgSkeleton } from '@/components/Image'
import { AppLayout } from '@/components/Layouts/AppLayout'
import MonthPicker, { formatAsQry, useMonthPicker } from '@/components/MonthPicker'
import { WinnerItem } from '@/components/WinnerItem'
import { useLanguage, useLayout } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { svgs } from '@/utils/Images'
import { Winner } from '@models/Index'
import { appUtils } from 'lib/AppUtils'
import { FC, Fragment, memo, useEffect } from 'react'
import useSWRImmutable from 'swr/immutable'

export default function Winners() {
  const {
    errors: { no_data },
  } = useLanguage()
  const { isDesktop } = useLayout()

  const { pageIndex, total, setTotal, list, setList, resetParams, loadMore } = useLoadMore()

  const { filter, setFilter, onApply, onReset } = useMonthPicker(resetParams)

  const { data, mutate, isValidating, isLoading } = useSWRImmutable(
    typeof isDesktop == 'undefined'
      ? null
      : `${API_URL.winners}?perPage=${isDesktop === false ? '6' : '12'}&page=${pageIndex + 1}${filter ? '&sort_by_month=' + formatAsQry(filter) : ''}`,
    appUtils.fetcher
  )

  useEffect(() => {
    if (!isValidating && !isLoading && data?.data?.length > 0) {
      setList(prev => data?.data?.reduce?.((arr, item) => arr.concat(item), prev))
      setTotal(data?.total)
    }
  }, [isValidating, isLoading, data])

  return (
    <AppLayout
      showBanner
      title='winners'
      titleOuterCls='sticky top-16 md:top-20 z-[51]' // z-index must be larger than LoadingView's
      titleInnerCls='flex items-center'
      filter={
        <Fragment>
          <MonthPicker valueCtrl={[filter, setFilter]} onApply={onApply} className='ml-auto' />
          <IconButton
            className='ml-2 h-8 w-8 p-1.5 md:ml-4'
            onClick={() =>
              onReset().then(() => {
                if (!isValidating && !isLoading) {
                  mutate()
                }
              })
            }
          >
            {svgs.reload}
          </IconButton>
        </Fragment>
      }
      outerCls='relative'
      innerCls='grid w-full animate-fade-in-down grid-cols-1 gap-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-4'
    >
      {list?.length > 0
        ? list.map((item: Winner, index: number) => (
            <WinnerItem
              key={'row-' + index}
              winner={item}
              disabled={false} // enable redirect to competition detail page
              className='animate-fade-in-down rounded-lg bg-white/100 pb-2 shadow-lg shadow-stone-200'
              imgCls='bg-stone-900 max-h-72 w-full object-contain'
              titleCls='px-2 mt-2 text-sm'
              dateCls='px-2 my-2 text-xs'
              priceCls='px-2 mt-auto text-xs'
            />
          ))
        : null}

      {isValidating || isLoading ? (
        <Skeletons />
      ) : data?.data?.length > 0 ? null : (
        <div className='col-span-full row-span-full animate-fade-in-down place-self-center py-24 text-center'>{no_data}</div>
      )}

      <LoadMoreButton className='col-span-full h-10 justify-self-center' onClick={loadMore} current={list?.length} total={total} />
    </AppLayout>
  )
}

const Skeletons: FC<{ numOfItems?: number }> = memo(function Skeleton({ numOfItems = 12 }) {
  const skeletons = []
  for (let index = 0; index < numOfItems; index++) {
    skeletons.push(
      <div key={index} className='card-container animate-fade-in-down'>
        <div className='skeleton-wrap'>
          <div className='skeleton-img-wrap h-52 rounded-t-lg'>
            <ImgSkeleton className='skeleton-img' />
          </div>
          <div className='skeleton-line mx-2 mt-2 h-4'></div>
          <div className='skeleton-line mx-2 mt-2 h-3 w-1/2'></div>
          <div className='skeleton-line mx-2 mb-2 mt-4 h-3 w-1/2'></div>
        </div>
      </div>
    )
  }
  return <Fragment>{skeletons}</Fragment>
})
